import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCheck, Trash2, AlertTriangle, Ban, DollarSign, Info, Check, XCircle, MessageCircle, Store, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { notificationService, type Notification } from "@/services/notificationService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/use-toast";
import { formatDistanceToNow } from "date-fns";

export function NotificationBell() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    loadNotifications();
    loadUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const loadNotifications = async () => {
    const { data } = await notificationService.getNotifications(10);
    if (data) setNotifications(data);
  };

  const loadUnreadCount = async () => {
    const { count } = await notificationService.getUnreadCount();
    setUnreadCount(count);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await notificationService.markAsRead(notificationId);
    await loadNotifications();
    await loadUnreadCount();
  };

  const handleMarkAllAsRead = async () => {
    await notificationService.markAllAsRead();
    await loadNotifications();
    await loadUnreadCount();
    toast({ title: "All notifications marked as read" });
  };

  const handleDelete = async (notificationId: string) => {
    const { error } = await notificationService.deleteNotification(notificationId);
    if (error) {
      toast({
        title: "Couldn't delete notification",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    await loadUnreadCount();
  };

  const getIcon = (icon: string) => {
    switch (icon) {
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'ban':
        return <Ban className="h-4 w-4 text-red-500" />;
      case 'dollar-sign':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'check':
        return <Check className="h-4 w-4 text-emerald-500" />;
      case 'x-circle':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'message-circle':
        return <MessageCircle className="h-4 w-4 text-purple-500" />;
      case 'store':
        return <Store className="h-4 w-4 text-amber-500" />;
      case 'shopping-cart':
        return <ShoppingCart className="h-4 w-4 text-emerald-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <motion.span
            animate={unreadCount > 0 ? { rotate: [0, -12, 10, -8, 6, 0] } : { rotate: 0 }}
            transition={unreadCount > 0 ? { duration: 0.7, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" } : undefined}
            className="inline-flex origin-top"
          >
            <Bell className="h-5 w-5" />
          </motion.span>
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 20 }}
                className="absolute -top-1 -right-1"
              >
                <Badge
                  className="h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-600 hover:bg-red-600"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="p-2 border-b flex items-center justify-between">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs h-7"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex flex-col items-start p-3 cursor-pointer ${
                  !notification.is_read ? 'bg-muted/50' : ''
                }`}
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <div className="flex items-start gap-2 w-full">
                  <div className="mt-0.5">
                    {getIcon(notification.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <div className="h-2 w-2 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
